import { Injectable } from '@angular/core';
import { downgradeToAJSService } from '../ib-commons-angularjs-utils';
import { IbStorageUtility, SessionConfig } from './ib-storage-utils.model';

@Injectable({
    providedIn: 'root'
})
export class CommonsSessionStorageService implements IbStorageUtility {

    private prefix = '';

    setPrefix (prefix: string): void {
        this.prefix = prefix;
    }

    isSupported (): boolean {
        return typeof window.sessionStorage !== 'undefined';
    }

    /**
     * @description Iberia utility to set an object to [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * Do not pass undefined or null values. If an undefined value is passed,
     * it will be converted to null to avoid errors when recovering the value and make it more consistent.
     * @param {string} key The key to set the value to. It will be prefixed by default if the config is set in the application
     * @param {unknown} value Any value to store, non-null or undefined. It will be serialized.
     * @param {SessionConfig} config  Optional config object to override if it has to use the prefix, true by default.
     * @example
     * storageService.set('key', value);
     * @author Iván Peña Huguet
     */
    set (key: string, value: NonNullable<unknown>, config: SessionConfig = { withPrefix: true } ): void {
        const valueToSave = JSON.stringify(typeof value === 'undefined' ? null : value);
        const keyToSave = config.withPrefix && this.prefix !== '' ? `${this.prefix}.${key}` : key;
        window.sessionStorage.setItem(keyToSave, valueToSave);
    }

    /**
     * @description Iberia utility to get an object from [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * It uses generic type to set the typing, see examples for more info. If no value is found, it returns null consistently.
     * @param {string} key The key to get the value from. It will be prefixed by default if the config is set in the application
     * @param {SessionConfig} config  Optional config object to override if it has to use the prefix, true by default.
     * @returns The value found or null if not found.
     * @example
     * storageService.get<MyType>('key'); // returns 'value' as MyType
     * storageService.get<string>('key'); // returns 'value' as string
     * storageService.get<string>('key', { withPrefix: false }); // returns 'value' as string and ignores the prefix
     * @author Iván Peña Huguet
     */
    get<T = unknown> (key: string, config: SessionConfig = { withPrefix: true } ): T | null {
        const keyToGet = config.withPrefix && this.prefix !== '' ? `${this.prefix}.${key}` : key;
        const storagedValue = window.sessionStorage.getItem(keyToGet);
        try {
            return JSON.parse( storagedValue ?? 'null') as T;
        } catch (err) {
            return storagedValue === '' ? null : storagedValue as unknown as T;
        }
    }

    /**
     * @description Iberia utility to remove a value from [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * @param {string} key The key to remove of the storage. It will be prefixed by default if the config is set in the application
     * @param {SessionConfig} config  Optional config object to override if it has to use the prefix, true by default.
     * @example
     * storageService.remove('key');
     * storageService.remove('key', { withPrefix: false });
     * @author Iván Peña Huguet
     */
    remove (key: string, config: SessionConfig = { withPrefix: true } ): void {
        const keyToRemove = config.withPrefix && this.prefix !== '' ? `${this.prefix}.${key}` : key;
        window.sessionStorage.removeItem(keyToRemove);
    }
    /**
     * @description Iberia utility to clear all variables that matches with a patter from [<b>session storage</b>]{@link https://developer.mozilla.org/es/docs/Web/API/Window/sessionStorage}.
     * @param {string | RegExp} pattern Optional value that provides a pattern (RegExp) or string in order to remove pairs which key matches the pattern
     *                                   If not provided this function will delete any key that starts with the configured prefix or all keys.
     * @param {SessionConfig} config  Optional config object to override if it has to use the prefix, true by default.
     * @example
     * storageService.clearAll(); //Remove all pairs which keys starts with configured prefix unless this prefix is undefine when will remove all pairs
     * storageService.clearAll('\[a-z]', { withPrefix: false }) //Remove all pairs which keys match the pattern (provided as string) and without considering a prefix for keys matching
     * storageService.clearAll(/[a-z]/, { withPrefix: false }) //Remove all pairs which keys match the pattern (provided as RegExp) and without considering a prefix for keys matching
     * storageService.clearAll(/[a-z]/) //Remove all pairs which keys match the pattern (provided as RegExp) and taking in account that keys to be removed must start with the configured prefix
     * @author Iván Peña Huguet
     */
    clearAll (pattern: string | RegExp = /(?:)/, config: SessionConfig = { withPrefix: true }): void {
        const prefixPattern = (config.withPrefix && this.prefix) ? new RegExp(`^${this.prefix}`) : new RegExp(/(?:)/);
        const patternAux = pattern ? new RegExp(pattern) : new RegExp(/(?:)/);
        for (const key in window.sessionStorage) {
            if (prefixPattern.test(key) && patternAux.test(key)) {
                window.sessionStorage.removeItem(key);
            }
        }
    }
}

downgradeToAJSService('CommonsSessionStorageService', CommonsSessionStorageService, 'ib-commons-utils-module');

